import yaml from 'js-yaml';
import isEqual from 'lodash/isEqual';
const EDITOR_MODE_STORAGE_KEY = 'mqttNgMapperConfigEditorMode';
const mapperSchema = {
  $id: 'niotix-dynamic-packet-mapper.schema.json',
  type: 'object',
  description: 'Dynamic packet mapper configuration for MQTT NG incoming packets.',
  additionalProperties: false,
  required: ['version', 'output', 'emit'],
  definitions: {
    transform: {
      description: 'Optional value conversion applied after resolving the value.',
      oneOf: [{
        enum: ['number', 'string', 'boolean', 'timestampIso']
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['multiply'],
        properties: {
          multiply: {
            type: 'number'
          }
        }
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['divide'],
        properties: {
          divide: {
            type: 'number'
          }
        }
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['scalePow10'],
        properties: {
          scalePow10: {}
        }
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['default'],
        properties: {
          default: {}
        }
      }]
    },
    valueExpression: {
      description: 'Literal value or expression that resolves a value from packet data, topic captures, or lookup tables.',
      oneOf: [{
        type: 'string'
      }, {
        type: 'number'
      }, {
        type: 'boolean'
      }, {
        type: 'null'
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['path'],
        properties: {
          path: {
            type: 'string',
            description: 'Path in the incoming packet. Use $root, $item, or $parent in fan-out mappings.'
          },
          transforms: {
            type: 'array',
            items: {
              $ref: '#/definitions/transform'
            }
          }
        }
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['capture'],
        properties: {
          capture: {
            type: 'string',
            description: 'Named topic capture from input.topicCaptures.'
          },
          transforms: {
            type: 'array',
            items: {
              $ref: '#/definitions/transform'
            }
          }
        }
      }, {
        type: 'object',
        additionalProperties: false,
        required: ['lookup'],
        properties: {
          lookup: {
            type: 'object',
            additionalProperties: false,
            required: ['table'],
            properties: {
              table: {
                type: 'string',
                description: 'Name of the lookup table under lookups.'
              },
              key: {
                description: 'Fixed lookup key.'
              },
              from: {
                $ref: '#/definitions/valueExpression',
                description: 'Expression that resolves the lookup key.'
              }
            }
          },
          transforms: {
            type: 'array',
            items: {
              $ref: '#/definitions/transform'
            }
          }
        }
      }]
    }
  },
  properties: {
    version: {
      const: 1,
      description: 'Mapper DSL version. Currently only version 1 is supported.'
    },
    output: {
      enum: ['direct', 'ddr'],
      description: 'Output packet shape emitted by this mapper.'
    },
    input: {
      type: 'object',
      description: 'Optional input selection and MQTT topic parsing settings.',
      additionalProperties: false,
      properties: {
        root: {
          type: 'string',
          description: 'Root selector for packet arrays, e.g. $[] or measurements[].values[].'
        },
        topicPattern: {
          type: 'string',
          description: 'Regular expression matched against the MQTT topic.'
        },
        topicCaptures: {
          type: 'object',
          description: 'Named capture-group indexes from topicPattern.',
          additionalProperties: {
            type: 'number'
          }
        }
      }
    },
    lookups: {
      type: 'object',
      description: 'Named lookup tables used by lookup value expressions.'
    },
    emit: {
      type: 'array',
      description: 'Each entry emits one or more output packets from the selected input scope.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['output'],
        properties: {
          foreach: {
            type: 'string',
            description: 'Optional array fan-out selector. $item points to each selected item.'
          },
          output: {
            type: 'object',
            additionalProperties: false,
            properties: {
              direct: {
                type: 'object',
                additionalProperties: false,
                required: ['dtwinId', 'updates'],
                properties: {
                  dtwinId: {
                    $ref: '#/definitions/valueExpression',
                    description: 'Digital twin id for the emitted packet.'
                  },
                  updates: {
                    type: 'array',
                    minItems: 1,
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      required: ['stateId', 'value'],
                      properties: {
                        stateId: {
                          $ref: '#/definitions/valueExpression'
                        },
                        value: {
                          $ref: '#/definitions/valueExpression'
                        },
                        time: {
                          $ref: '#/definitions/valueExpression'
                        },
                        valueType: {
                          $ref: '#/definitions/valueExpression'
                        }
                      }
                    }
                  }
                }
              },
              ddr: {
                type: 'object',
                additionalProperties: false,
                required: ['routing', 'variable', 'data'],
                properties: {
                  routing: {
                    description: 'DDR routing object or expression.'
                  },
                  variable: {
                    $ref: '#/definitions/valueExpression'
                  },
                  data: {
                    type: 'object',
                    additionalProperties: false,
                    required: ['value'],
                    properties: {
                      value: {
                        $ref: '#/definitions/valueExpression'
                      },
                      time: {
                        $ref: '#/definitions/valueExpression'
                      },
                      valueType: {
                        $ref: '#/definitions/valueExpression'
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
};
function initialMode() {
  let saved;
  try {
    saved = typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(EDITOR_MODE_STORAGE_KEY);
  } catch (_) {
    saved = null;
  }
  return saved === 'yaml' ? 'yaml' : 'json';
}
function stringifyConfig(value, mode) {
  if (mode === 'yaml') {
    return yaml.dump(value || {}, {
      noRefs: true,
      lineWidth: 120
    }).trimEnd();
  }
  return JSON.stringify(value || {}, null, 2);
}
function parseConfig(text, mode) {
  if (!text.trim()) {
    return undefined;
  }
  return mode === 'yaml' ? yaml.load(text) : JSON.parse(text);
}
export default {
  name: 'MapperConfigEditor',
  props: {
    value: {
      type: Object,
      required: false,
      default: undefined
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    const mode = initialMode();
    return {
      mode,
      text: stringifyConfig(this.value, mode),
      parseError: '',
      monaco: null,
      editor: null,
      mapperModelUri: null,
      mapperModel: null,
      monacoReady: false,
      monacoFailed: false,
      monacoError: ''
    };
  },
  watch: {
    value(nextValue) {
      try {
        if (isEqual(parseConfig(this.text, this.mode), nextValue)) {
          return;
        }
      } catch (_) {
        // Keep invalid in-progress edits untouched until the parent sends a real external value change.
      }
      const nextText = stringifyConfig(nextValue, this.mode);
      if (nextText !== this.text) {
        this.text = nextText;
        this.editor && this.editor.setValue(nextText);
      }
    },
    disabled(value) {
      this.editor && this.editor.updateOptions({
        readOnly: value
      });
    }
  },
  mounted() {
    this.mountMonaco();
  },
  beforeDestroy() {
    this.editor && this.editor.dispose();
    this.mapperModel && this.mapperModel.dispose && this.mapperModel.dispose();
  },
  methods: {
    async mountMonaco() {
      try {
        this.monacoFailed = false;
        this.monacoError = '';
        const monaco = await import('monaco-editor/esm/vs/editor/editor.api');
        this.monaco = monaco;
        const mapperModelUri = monaco.Uri && monaco.Uri.parse('inmemory://model/mapper-config.json');
        this.mapperModelUri = mapperModelUri;
        if (monaco.languages.json && monaco.languages.json.jsonDefaults) {
          monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: true,
            schemas: [{
              uri: mapperSchema.$id,
              fileMatch: [mapperModelUri.toString()],
              schema: mapperSchema
            }]
          });
        }
        this.mapperModel = monaco.editor.createModel ? monaco.editor.createModel(this.text, this.mode === 'yaml' ? 'yaml' : 'json', mapperModelUri) : null;
        this.editor = monaco.editor.create(this.$refs.monacoHost, {
          value: this.mapperModel ? undefined : this.text,
          model: this.mapperModel || undefined,
          language: this.mode === 'yaml' ? 'yaml' : 'json',
          minimap: {
            enabled: false
          },
          automaticLayout: true,
          readOnly: this.disabled,
          scrollBeyondLastLine: false
        });
        this.editor.onDidChangeModelContent(() => {
          this.text = this.editor.getValue();
          this.emitParsedValue();
        });
        this.monacoReady = true;
      } catch (error) {
        this.monacoReady = false;
        this.monacoFailed = true;
        this.monacoError = error.message;
      }
    },
    setEditorText(text) {
      this.text = text;
      this.editor && this.editor.setValue(text);
    },
    switchMode(nextMode) {
      if (nextMode !== 'json' && nextMode !== 'yaml') {
        return;
      }
      let parsed;
      try {
        parsed = parseConfig(this.text, this.mode);
      } catch (error) {
        this.parseError = `Cannot switch mode: ${error.message}`;
        return;
      }
      this.mode = nextMode;
      try {
        typeof window !== 'undefined' && window.localStorage && window.localStorage.setItem(EDITOR_MODE_STORAGE_KEY, nextMode);
      } catch (_) {
        // Mode still changes in memory when browser storage is unavailable.
      }
      this.setEditorText(stringifyConfig(parsed, nextMode));
      if (this.editor && this.monaco) {
        this.monaco.editor.setModelLanguage(this.editor.getModel(), nextMode === 'yaml' ? 'yaml' : 'json');
      }
      this.emitParsedValue();
    },
    formatCurrentText() {
      let parsed;
      try {
        parsed = parseConfig(this.text, this.mode);
      } catch (error) {
        this.parseError = error.message;
        return;
      }
      this.setEditorText(stringifyConfig(parsed, this.mode));
      this.emitParsedValue();
    },
    emitParsedValue() {
      let parsed;
      try {
        parsed = parseConfig(this.text, this.mode);
      } catch (error) {
        this.parseError = error.message;
        return;
      }
      if (parsed === undefined) {
        this.parseError = '';
        this.$emit('input', undefined);
        return;
      }
      if (parsed !== undefined && (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))) {
        this.parseError = 'Mapper config must be an object';
        return;
      }
      this.parseError = '';
      this.$emit('input', parsed);
    }
  }
};